// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { LayersEnum } from '../../Chart/Renderers/TerceraChartBaseRenderer';
import { DataCacheToolType, DataCacheToolTypeName, GetGroupByToolType, GetToolTypesByGroup } from '../../Commons/cache/DataCacheToolEnums';
import { TerceraChartAction, TerceraChartActionEnum } from '../../Chart/TerceraChartAction';
import { ThemeManager } from '../misc/ThemeManager';
import { contextMenuHandler } from '../../Utils/AppHandlers';
import { TerceraChartActionProcessor } from '../../Chart/TerceraChartActionProcessor';
import { Resources } from '../../Commons/properties/Resources';
import { TerceraChartToolsPanelTemplate } from '../../templates';
import { TerceraButtonsPopup } from './TerceraButtonsPopup';
import { ContainerControl } from './ContainerControl';
import { ControlsUtils } from '../UtilsClasses/ControlsUtils';
import { ControlsTypes } from '../UtilsClasses/FactoryConstants';

export class TerceraChartToolsPanel extends ContainerControl.extend({
    template: TerceraChartToolsPanelTemplate,
    dropDownMenuTimeoutID: null,
    chartOEOffset: 125,
    movingSize: 30,
    toolbarOffset: 3,
    popupMaxColumns: 9,
    data () {
        return {
            isVertical: true,
            isLeftOrTop: true,
            movable: true,
            isPosAbsolute: true,
            actionProcessor: null
        };
    }
}) {
    getType (): string { return ControlsTypes.TerceraChartToolsPanel; }

    oninit (): void {
        super.oninit();

        super.observe('actionProcessor', this.onActionProcessorChanged, { init: false });
        super.on('onPopupButtonClick', this.onPopupButtonClick);
        super.on('onMovingBtnClick', this.onMovingBtnClick);

        super.observe('left top', this.onDragToolbar);
        super.observe('onProcessMove', this.onMovingChanged);

        super.setBounds(0, super.movingSize, super.movingSize, super.movingSize);
    }

    onDragToolbar (): void {
        if (super.get('onProcessMove') === false) { return; }

        const x = super.get('left');
        const y = super.get('top');

        const w = super.get('width');
        const h = super.get('height');

        const container = super.getClientPanel();

        if (container == null) { return; }

        const bottom = container.offsetHeight - y - h;
        const right = container.offsetWidth - x - w;

        const isVertical = (x < y && x < bottom) || (right < y && right < bottom);

        super.set({
            isVertical,
            isLeftOrTop: (x < right && isVertical) || (y < bottom && !isVertical)
        });
    }

    onMovingChanged (moving: boolean): void {
        if (moving) { return; }
        const container = super.getClientPanel();
        if (container == null) { return; }

        const z = super.toolbarOffset;
        const s = super.movingSize;
        const w = container.offsetWidth - s - 4;
        const h = container.offsetHeight - s - 1;

        const isVertical: boolean = super.get('isVertical');
        const isLeftOrTop: boolean = super.get('isLeftOrTop');

        const x = isLeftOrTop ? (isVertical ? 3 : z) : (isVertical ? w : 0);
        const y = isVertical ? s : (isLeftOrTop ? s - z : h);

        const needOffsetFromOE: boolean = isVertical && !isLeftOrTop && super.get('chartOEVisible');
        // if (needOffsetFromOE && super.chartOEOffset === null) { super.chartOEOffset = container.getElementsByClassName('chart-order-entry')[0]?.clientWidth; }
        const offsetFromOE = needOffsetFromOE ? super.chartOEOffset : 0;
        super.setLocation(x - offsetFromOE, y);
    }

    onMovingBtnClick (event): void {
        super.onMouseDown(event, true);
    }

    onItemClicked (item): void {
        if (item?.event) item.event(item.type);
    }

    onPopupButtonClick (popupBtn): void {
        TerceraButtonsPopup.show(
            super.get('popupItems'),
            popupBtn.getX(),
            popupBtn.getY() + 30,
            super.popupMaxColumns
        );
    }

    showDropDownMenu (action, event): void {
        const aD = action?.Details;
        const groupType = GetGroupByToolType(aD);
        let items: any[] = GetToolTypesByGroup(groupType);
        let longestText = '';
        items = items.map((t) => {
            const action = TerceraChartAction.Create(
                TerceraChartActionEnum.CreationTool,
                t
            );
            const lKey = TerceraChartActionProcessor.ToolStateInfoDict[t].locKey;
            const txt = Resources.getResource(lKey);
            if (txt.length > longestText.length) {
                longestText = txt;
            }
            return {
                tag: t,
                type: action,
                style: `js-drawingTool-${DataCacheToolTypeName[t]}`,
                name: DataCacheToolTypeName[t],
                locKey: lKey,
                text: txt,
                tooltip: txt,
                event: this.onToolStripItemClicked.bind(this, action)
            };
        });

        const isVertical: boolean = super.get('isVertical');
        const isLeftOrTop: boolean = super.get('isLeftOrTop');
        const isBottom: boolean = !isVertical && !isLeftOrTop;
        const isRight: boolean = isVertical && !isLeftOrTop;
        const h = event.node.offsetHeight;
        const w = event.node.offsetWidth;
        const original = event.original;
        const imgW = 55;
        const menuW = ControlsUtils.GetTextWidth(
            longestText,
            ThemeManager.Fonts.Font_12F_regular
        ) + imgW;

        const menuX = original.x - original.offsetX + (isVertical ? (isRight ? -menuW - 15 : w) : 0);
        const menuY = original.y - original.offsetY + (isVertical ? 0 : (isBottom ? -25 * items.length : h));

        contextMenuHandler.Show(items, menuX, menuY, { width: menuW });
    }

    onHoverToolEvent (caller, event): void {
        if (super.dropDownMenuTimeoutID) clearTimeout(super.dropDownMenuTimeoutID);

        super.dropDownMenuTimeoutID = setTimeout(() => {
            this.showDropDownMenu(caller.type, event);
        }, 0);
    }

    createDefaultItems (): any[] {
        const callback = this.onToolStripItemClicked.bind(this);
        const onHoverEvent = this.onHoverToolEvent.bind(this);

        return [
            { visible: true, name: 'Cancel', type: TerceraChartAction.Create(TerceraChartActionEnum.Cancel), event: callback },

            { visible: true, name: 'ToolSelector', type: TerceraChartAction.Create(TerceraChartActionEnum.ToolSelector), event: callback },
            // { visible: true, name: 'Eraser', type: TerceraChartAction.Create(TerceraChartActionEnum.Eraser), event: callback },

            { name: 'separator' },

            { visible: true, name: 'SnapToCandle', type: TerceraChartAction.Create(TerceraChartActionEnum.SnapToCandle), event: callback },
            { visible: true, name: 'SetStayInDrawingMode', type: TerceraChartAction.Create(TerceraChartActionEnum.SetStayInDrawingMode), event: callback },

            { name: 'separator' },

            // Lines tools // { visible: true, name: DataCacheToolTypeName[DataCacheToolType.TrendTools], type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.Line), event: callback, hoverEvent: onHoverEvent },
            { visible: true, name: DataCacheToolTypeName[DataCacheToolType.HorizontalLine], type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.HorizontalLine), event: callback, hoverEvent: onHoverEvent },

            { name: 'separator' },

            // { visible: true, name: DataCacheToolTypeName[DataCacheToolType.GeometryTools], type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.Triangle), event: callback, hoverEvent: onHoverEvent },
            { visible: true, name: DataCacheToolTypeName[DataCacheToolType.Triangle], type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.Triangle), event: callback, hoverEvent: onHoverEvent },

            { name: 'separator' },

            // Fibonacci // { visible: true, name: DataCacheToolTypeName[DataCacheToolType.FibonacciTools], type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciRetracement), event: callback, hoverEvent: onHoverEvent },
            { visible: true, name: DataCacheToolTypeName[DataCacheToolType.FibonacciRetracement], type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciRetracement), event: callback, hoverEvent: onHoverEvent },

            { name: 'separator' },

            // Gann // { visible: true, name: DataCacheToolTypeName[DataCacheToolType.GannTools], type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.GannLine), event: callback, hoverEvent: onHoverEvent },
            { visible: true, name: DataCacheToolTypeName[DataCacheToolType.GannLine], type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.GannLine), event: callback, hoverEvent: onHoverEvent },

            { name: 'separator' },

            // Elliott  Wave name: DataCacheToolTypeName[DataCacheToolType.ElliottTools]
            { visible: true, name: DataCacheToolTypeName[DataCacheToolType.ElliottImpulse], type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.ElliottImpulse), event: callback, hoverEvent: onHoverEvent },

            { name: 'separator' },

            { visible: true, name: 'InfoLine', type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.InfoLine), event: callback },

            { name: 'separator' },

            { visible: true, name: 'DeleteHoverAndSelectedTools', type: TerceraChartAction.Create(TerceraChartActionEnum.DeleteHoverAndSelectedTools), event: callback }
        ];
    }

    onActionProcessorChanged (newProc, oldProc): void {
        if (oldProc) oldProc.StateChanged.UnSubscribe(this.updateToolbar, this);
        if (newProc) newProc.StateChanged.Subscribe(this.updateToolbar, this);

        if (!newProc) return;

        this.updateToolbar();
    }

    onResize (): void {
        let curControlLeft = 0;
        const controlMargin = 3;
        const popupBtn = 35;
        const drawingSeparator = 1;
        const drawingBtn = 30;

        const el = super.find('*');
        let parentWidth = 0;

        if (el?.parentElement) parentWidth = el.parentElement.offsetWidth - popupBtn;

        this.zeroingPopup();
        const items = super.get('toolStipItems') || [];
        if (!items.length) return;

        const newPopupItems = [];
        const len = items.length;

        for (let i = 0; i < len; i++) {
            const item = items[i];
            const isSeparator = this.isSeparator(item);
            const controlW = isSeparator ? drawingSeparator : drawingBtn;
            item.visible = true;

            const curControlRight = curControlLeft + controlW;
            if (curControlRight > parentWidth) {
                if (!isSeparator) newPopupItems.push(Object.assign({}, item));

                item.visible = false;
            }

            curControlLeft = curControlRight + controlMargin;
        }

        if (newPopupItems.length > 0) super.set('showPopupButton', true);

        super.set({
            toolStipItems: items,
            popupItems: newPopupItems
        });

        this.onMovingChanged(false);
    }

    isSeparator (item): boolean {
        return item && item.name === 'separator';
    }

    zeroingPopup (): void {
        super.set({
            popupItems: null,
            showPopupButton: false
        });
    }

    updateToolbar (): void {
        const actionProcessor = super.get('actionProcessor');
        if (!actionProcessor) return;

        let toolStripItems = super.get('toolStipItems');
        // First init for optimization purposes.
        if (!toolStripItems) toolStripItems = this.createDefaultItems();

        const len = toolStripItems.length;
        for (let i = 0; i < len; i++) {
            const tsItem = toolStripItems[i];
            if (tsItem.name === 'separator') continue;

            const action = tsItem.type;
            const state = actionProcessor.GetTerceraChartActionState(action);

            tsItem.active = state.Active;
            tsItem.enabled = state.Enabled;
            tsItem.visible = state.Visible;
            tsItem.tooltip = state.locKey;
        }
        actionProcessor.chart.IsDirty(LayersEnum.Tools);
        super.set('toolStipItems', toolStripItems);
        this.onResize();
    }

    onToolStripItemClicked (action, caller): void {
        this.onGroupBtnClicked(action, caller);

        super.get('actionProcessor').ProcessTerceraChartAction(action);
    }

    onGroupBtnClicked (action, caller): any {
        const toolType = action.Details;
        const groupType = GetGroupByToolType(toolType);

        if (groupType != null) {
            // may clicked on new tool from Drawing Group btn context menu => need to change group btn image and events
            super.set('toolStipItems', super.get('toolStipItems').map((item) => {
                const itemToolType = item.type?.Details;
                if (caller != null && groupType === GetGroupByToolType(itemToolType) && toolType !== itemToolType) {
                    item.name = caller.name;
                    item.type = TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, toolType); // change toolStipItem
                    item.event = this.onToolStripItemClicked.bind(this);
                    item.hoverEvent = this.onHoverToolEvent.bind(this);
                }
                return item;
            }));
        }
    }

    dispose (): void {
        const actionProcessor = super.get('actionProcessor');
        if (actionProcessor != null) { actionProcessor.StateChanged.UnSubscribe(this.updateToolbar, this); }

        super.dispose();
    }

    themeChange (): void {
        this.updateToolbar();
    }

    localize (): void {}
}
